import {
  ChevronLeft,
  ChevronRight,
  CornerDownLeft,
  ExternalLink,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getAffinityChartNodeVisualSrc } from "@/features/affinity-chart/affinity-chart-node-visuals";
import type { AffinityChartDetailView } from "@/features/affinity-chart/affinity-chart.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

type AffinityChartDetailPanelProps = {
  collapsed?: boolean;
  detail: AffinityChartDetailView;
  onClearSelection: () => void;
  onOpenRecord: (href: string) => void;
  onReturnFocus: () => void;
  onToggleCollapsed: () => void;
};

export function AffinityChartDetailPanel({
  collapsed = false,
  detail,
  onClearSelection,
  onOpenRecord,
  onReturnFocus,
  onToggleCollapsed,
}: AffinityChartDetailPanelProps) {
  const { t } = useTranslation();

  if (collapsed) {
    return (
      <aside className="flex items-start justify-center border-l border-ink/10 pl-2 pt-2 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)]">
        <div className="flex items-center gap-1">
          <button
            aria-label={t("affinityChart.detail.expandPanel", {
              defaultValue: "Expand selection details",
            })}
            className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
            onClick={onToggleCollapsed}
            type="button"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="[writing-mode:vertical-rl] text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">
            {t("affinityChart.detail.eyebrow")}
          </span>
        </div>
      </aside>
    );
  }

  return (
    <aside className="min-h-0 border-l border-ink/10 pl-5 xl:sticky xl:top-28 xl:h-[calc(100vh-8rem)] xl:max-h-[calc(100vh-8rem)] xl:overflow-hidden">
      <div className="flex h-full min-h-0 flex-col gap-5">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
              {t("affinityChart.detail.eyebrow")}
            </p>
            <h2 className="font-display text-[2rem] leading-none tracking-tight text-foreground">
              {detail.kind === "empty"
                ? t(detail.titleKey)
                : detail.kind === "node"
                  ? detail.node.label
                  : detail.edge.label}
            </h2>
          </div>
          <div className="flex items-center gap-1">
            <button
              aria-label={t("affinityChart.detail.collapsePanel", {
                defaultValue: "Collapse selection details",
              })}
              className="inline-flex h-8 items-center justify-center px-1 text-muted-foreground transition-colors hover:text-foreground"
              onClick={onToggleCollapsed}
              type="button"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <Button
              aria-label="Clear selection"
              onClick={onClearSelection}
              size="icon"
              type="button"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {detail.kind === "empty" ? (
            <div className="space-y-4 rounded-[1.35rem] bg-[#f2e7d6]/72 px-4 py-4">
              <p className="leading-7 text-muted-foreground">{t(detail.descriptionKey)}</p>
              <Button onClick={onReturnFocus} type="button" variant="outline">
                <CornerDownLeft className="h-4 w-4" />
                {t("affinityChart.actions.returnToGraph")}
              </Button>
            </div>
          ) : null}

          {detail.kind === "node" ? (
            <div className="space-y-5 rounded-[1.35rem] bg-[#f2e7d6]/72 px-4 py-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{t(detail.node.typeLabelKey)}</Badge>
                {detail.node.canonStatusLabelKey ? (
                  <Badge variant="outline">{t(detail.node.canonStatusLabelKey)}</Badge>
                ) : null}
              </div>

              <div className="overflow-hidden rounded-[1.2rem] border border-white/75 bg-[#fffaf2]">
                <img
                  alt={`${detail.node.label} preview`}
                  className="h-40 w-full object-cover"
                  src={getAffinityChartNodeVisualSrc(detail.node)}
                />
              </div>

              <p className="text-sm leading-7 text-muted-foreground">
                {detail.node.summaryText || t("affinityChart.detail.noSummary")}
              </p>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => onOpenRecord(detail.node.href)}
                  type="button"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("affinityChart.actions.openFullRecord")}
                </Button>
                <Button onClick={onReturnFocus} type="button" variant="outline">
                  <CornerDownLeft className="h-4 w-4" />
                  {t("affinityChart.actions.returnToGraph")}
                </Button>
              </div>
            </div>
          ) : null}

          {detail.kind === "edge" ? (
            <div className="space-y-5 rounded-[1.35rem] bg-[#f2e7d6]/72 px-4 py-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">{t(detail.edge.relationTypeLabelKey)}</Badge>
              </div>

              <div className="space-y-3 text-sm leading-7 text-muted-foreground">
                <p>
                  <span className="font-semibold text-foreground">{t("affinityChart.detail.source")}:</span>{" "}
                  {detail.sourceNode.label}
                </p>
                <p>
                  <span className="font-semibold text-foreground">{t("affinityChart.detail.target")}:</span>{" "}
                  {detail.targetNode.label}
                </p>
                <p>
                  <span className="font-semibold text-foreground">{t("affinityChart.detail.relationshipLabel")}:</span>{" "}
                  {detail.edge.label}
                </p>
                <p>
                  <span className="font-semibold text-foreground">{t("affinityChart.detail.relationshipType")}:</span>{" "}
                  {t(detail.edge.relationTypeLabelKey)}
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => onOpenRecord(detail.sourceNode.href)}
                  type="button"
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("affinityChart.actions.openSourceRecord")}
                </Button>
                <Button onClick={onReturnFocus} type="button" variant="outline">
                  <CornerDownLeft className="h-4 w-4" />
                  {t("affinityChart.actions.returnToGraph")}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

      </div>
    </aside>
  );
}
